import apiClient from './api.service';

export interface StockTransferItem {
  id: string;
  transfer_id: string;
  line_number: number;
  item_id: string;
  item_code?: string | null;
  item_description?: string | null;
  transfer_quantity: number;
  shipped_quantity: number;
  received_quantity: number;
  uom?: string | null;
  unit_cost: number;
  total_cost: number;
  heat_number?: string | null;
  dimension?: string | null;
  notes?: string | null;
}

export interface StockTransfer {
  id: string;
  company_id: string;
  transfer_number: string;
  transfer_date: string;
  transfer_type: string;
  from_warehouse_id: string;
  to_warehouse_id: string;
  project_id?: string | null;
  from_project_id?: string | null;
  to_project_id?: string | null;
  department_id?: string | null;
  transfer_status: string;
  approval_status: string;
  approval_instance_id?: string | null;
  approved_by?: string | null;
  approved_date?: string | null;
  shipped_by?: string | null;
  shipped_date?: string | null;
  received_by?: string | null;
  received_date?: string | null;
  notes?: string | null;
  reason?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  ship_journal_voucher_id?: string | null;
  receive_journal_voucher_id?: string | null;
  items: StockTransferItem[];
}

export interface CreateStockTransferDTO {
  transfer_date: string;
  from_warehouse_id: string;
  to_warehouse_id: string;
  project_id?: string | null;
  from_project_id?: string | null;
  to_project_id?: string | null;
  department_id?: string;
  transfer_type?: string | null;
  reason?: string | null;
  notes?: string | null;
  items: Array<{
    item_id: string;
    item_code?: string;
    item_description?: string;
    transfer_quantity: number;
    uom?: string;
    unit_cost?: number;
    heat_number?: string | null;
    dimension?: string | null;
    notes?: string | null;
  }>;
}

class StockTransferService {
  async list(filters?: {
    status?: string;
    fromWarehouseId?: string;
    toWarehouseId?: string;
    project_id?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<StockTransfer[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.fromWarehouseId) params.append('fromWarehouseId', filters.fromWarehouseId);
    if (filters?.toWarehouseId) params.append('toWarehouseId', filters.toWarehouseId);
    if (filters?.project_id) params.append('project_id', filters.project_id);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    const response = await apiClient.get(`/inventory/stock-transfers?${params.toString()}`);
    return response.data || [];
  }

  async getById(id: string): Promise<StockTransfer> {
    const response = await apiClient.get(`/inventory/stock-transfers/${id}`);
    return response.data;
  }

  async create(data: CreateStockTransferDTO): Promise<StockTransfer> {
    const response = await apiClient.post('/inventory/stock-transfers', data);
    return response.data;
  }

  async update(id: string, data: CreateStockTransferDTO): Promise<StockTransfer> {
    const response = await apiClient.put(`/inventory/stock-transfers/${id}`, data);
    return response.data;
  }

  async submit(id: string): Promise<StockTransfer> {
    const response = await apiClient.post(`/inventory/stock-transfers/${id}/submit`);
    return response.data;
  }

  async approve(id: string, remarks?: string): Promise<StockTransfer> {
    const response = await apiClient.post(`/inventory/stock-transfers/${id}/approve`, { remarks });
    return response.data;
  }

  async rejectApproval(id: string, payload: unknown): Promise<StockTransfer> {
    const response = await apiClient.post(`/inventory/stock-transfers/${id}/reject-approval`, payload);
    return response.data;
  }

  async ship(id: string): Promise<StockTransfer> {
    const response = await apiClient.post(`/inventory/stock-transfers/${id}/ship`);
    return response.data;
  }

  async receive(
    id: string,
    lines?: Array<{ line_id: string; receive_quantity: number }>
  ): Promise<StockTransfer> {
    const response = await apiClient.post(`/inventory/stock-transfers/${id}/receive`, { lines });
    return response.data;
  }

  async cancel(id: string): Promise<StockTransfer> {
    const response = await apiClient.post(`/inventory/stock-transfers/${id}/cancel`);
    return response.data;
  }
}

export const stockTransferService = new StockTransferService();
export default stockTransferService;
